import { LoadingOutlined } from "@ant-design/icons";
import { Form, FormItemProps, Input, Spin } from "antd";
import React, { useMemo, useRef, useState } from "react";
import {
    Controller,
    type FieldPath,
    type FieldValues,
    type RegisterOptions,
    useFormContext,
} from "react-hook-form";

type RHFTextFieldProps<TFieldValues extends FieldValues> = {
    name: FieldPath<TFieldValues>;
    rules?: RegisterOptions<TFieldValues, FieldPath<TFieldValues>>;
    onAfterChange?: (value?: string) => void;

    onAfterChangeAsync?: (value?: string) => Promise<unknown>;
    loading?: boolean;
    debounceMs?: number;
    disabled?: boolean;

    label?: React.ReactNode;
    help?: React.ReactNode;
    extra?: React.ReactNode;
    required?: boolean;
    tooltip?: React.ReactNode;

    fullWidth?: boolean;
    type?: React.HTMLInputTypeAttribute;
    formItemProps?: FormItemProps;
} & Omit<
    React.ComponentProps<typeof Input>,
    "name" | "defaultValue" | "type" | "onChange" | "value"
>;

export const RHFTextField = <TFieldValues extends FieldValues>({
    name,
    rules,
    onAfterChange,
    onAfterChangeAsync,
    loading,
    debounceMs = 0,
    disabled = false,

    label,
    help,
    extra,
    required,
    tooltip,

    fullWidth = true,
    type = "text",
    formItemProps,

    ...restInputProps
}: RHFTextFieldProps<TFieldValues>) => {
    const { control, formState } = useFormContext<TFieldValues>();
    const allDisabled = disabled || formState.isSubmitting;

    const [pending, setPending] = useState(false);
    const timerRef = useRef<number | null>(null);

    const handleAfterChange = useMemo(() => {
        return (value?: string) => {
            if (timerRef.current) {
                window.clearTimeout(timerRef.current);
                timerRef.current = null;
            }

            const run = async () => {
                try {
                    onAfterChange?.(value);
                    if (onAfterChangeAsync) {
                        setPending(true);
                        await onAfterChangeAsync(value);
                    }
                } finally {
                    if (onAfterChangeAsync) setPending(false);
                }
            };

            if (debounceMs > 0) {
                timerRef.current = window.setTimeout(run, debounceMs);
            } else {
                void run();
            }
        };
    }, [onAfterChange, onAfterChangeAsync, debounceMs]);

    const isLoading = loading ?? pending;
    const suffix = isLoading ? (
        <Spin size="small" indicator={<LoadingOutlined spin />} />
    ) : (
        restInputProps.suffix
    );

    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field, fieldState }) => {
                const validateStatus = fieldState.error ? "error" : undefined;
                const mergedHelp = fieldState.error?.message ?? help;

                return (
                    <Form.Item
                        {...formItemProps}
                        label={label}
                        help={mergedHelp}
                        extra={extra}
                        required={required}
                        tooltip={tooltip}
                        validateStatus={validateStatus}
                        style={{
                            ...(fullWidth ? { width: "100%" } : undefined),
                            margin: 0,
                            ...(formItemProps?.style ?? {}),
                        }}
                    >
                        <Input
                            {...restInputProps}
                            disabled={allDisabled}
                            value={field.value ?? ""}
                            type={type}
                            suffix={suffix}
                            onWheel={(e) => {
                                (e.currentTarget as HTMLInputElement).blur();
                            }}
                            onChange={(e) => {
                                field.onChange(e);
                                handleAfterChange(e.target.value);
                            }}
                            onBlur={(e) => {
                                field.onBlur();
                                restInputProps.onBlur?.(e);
                            }}
                        />
                    </Form.Item>
                );
            }}
        />
    );
};
